import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

const FinancialConsultantChat = ({ conversationId = 'admin-ai-dashboard', height = 420 }) => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showEvidenceById, setShowEvidenceById] = useState({});
  const scrollRef = useRef(null);

  const quickQuestions = [
    'Quem é o meu melhor cliente?',
    'Qual o cliente com maior valor pendente?',
    'Top 10 das compras',
    'Top 5 fornecedores',
    'Qual a compra com maior valor?'
  ];

  useEffect(() => {
    try {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    } catch {
      // ignore
    }
  }, [messages.length, loading]);

  const send = async (q) => {
    const text = (q ?? '').trim();
    if (!text || loading) return;

    setInput('');
    setLoading(true);

    // optimistic user bubble
    setMessages(prev => ([...prev, { id: `${Date.now()}-u`, role: 'user', text, documents: [] }]));

    try {
      const res = await api.post('/AdminAI/financial-chat', {
        query: text,
        conversationId,
        mode: 'consultant',
        includeEvidence: true
      });
      setMessages(prev => ([
        ...prev,
        {
          id: `${Date.now()}-a`,
          role: 'assistant',
          text: res.data?.response || 'Sem resposta.',
          documents: Array.isArray(res.data?.documents) ? res.data.documents : [],
          report: res.data?.report || null
        }
      ]));
    } catch (e) {
      console.error('financial-chat error', e);
      toast.error('Não consegui analisar essa questão.');
    } finally {
      setLoading(false);
    }
  };

  const onSend = () => send(input);

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      overflow: 'hidden'
    }}>
      <div style={{
        padding: '12px 14px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <div style={{ fontWeight: 900, color: '#111827' }}>💰 Consultor Financeiro IA</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Pergunte sobre vendas, compras, devedores e relatórios.</div>
        </div>
      </div>

      <div ref={scrollRef} style={{ height, padding: 14, overflowY: 'auto', background: '#eef2ff' }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#6b7280', marginTop: 70 }}>
            <div style={{ fontSize: 34, marginBottom: 12 }}>💬</div>
            <div style={{ fontWeight: 800 }}>Sugestões</div>
            <div style={{ marginTop: 12, display: 'grid', gap: 8, maxWidth: 520, marginInline: 'auto' }}>
              {quickQuestions.map(q => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  disabled={loading}
                  style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: '10px 12px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontWeight: 800,
                    color: '#111827'
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {messages.map(m => {
              const isUser = m.role === 'user';
              const hasReport = !isUser && m.report && typeof m.report === 'object';
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '82%',
                    background: isUser ? '#22c55e' : '#ffffff',
                    color: isUser ? '#ffffff' : '#111827',
                    borderRadius: 16,
                    padding: '10px 12px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                    border: isUser ? 'none' : '1px solid #e5e7eb'
                  }}>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: 14 }}>{m.text}</div>

                    {hasReport && (
                      <div style={{ marginTop: 10, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
                        {m.report?.executiveSummaryMarkdown && (
                          <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                            {m.report.executiveSummaryMarkdown}
                          </div>
                        )}

                        {Array.isArray(m.report?.kpis) && m.report.kpis.length > 0 && (
                          <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                            {m.report.kpis.slice(0, 8).map((k) => (
                              <div key={k.key} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12, padding: 10 }}>
                                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 900 }}>{k.label}</div>
                                <div style={{ fontSize: 14, fontWeight: 900, color: '#0f172a', marginTop: 2 }}>{k.value}</div>
                                {k.hint && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{k.hint}</div>}
                              </div>
                            ))}
                          </div>
                        )}

                        {Array.isArray(m.report?.alerts) && m.report.alerts.length > 0 && (
                          <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 900 }}>⚠️ Alertas</div>
                            {m.report.alerts.map((a, idx) => (
                              <div key={idx} style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: 10 }}>
                                <div style={{ fontWeight: 900, color: '#9a3412' }}>{a.title}</div>
                                {a.detail && <div style={{ fontSize: 12, color: '#7c2d12', marginTop: 2 }}>{a.detail}</div>}
                                {a.openUrl && (
                                  <button
                                    style={{ marginTop: 8, background: '#ea580c', color: '#fff', border: 'none', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontWeight: 900 }}
                                    onClick={() => window.open(a.openUrl, '_blank')}
                                  >
                                    Abrir
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {Array.isArray(m.report?.actions) && m.report.actions.length > 0 && (
                          <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 900 }}>✅ Ações recomendadas</div>
                            {m.report.actions.map((a) => (
                              <div key={a.id} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 12, padding: 10 }}>
                                <div style={{ fontWeight: 900, color: '#0f172a' }}>{a.title}</div>
                                {a.description && <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{a.description}</div>}
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                                  {a.openUrl && (
                                    <button
                                      style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontWeight: 900 }}
                                      onClick={() => window.open(a.openUrl, '_blank')}
                                    >
                                      Abrir
                                    </button>
                                  )}
                                  {a.downloadUrl && (
                                    <button
                                      style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontWeight: 900 }}
                                      onClick={() => window.open(a.downloadUrl, '_blank')}
                                    >
                                      Download
                                    </button>
                                  )}
                                  {a.mailto && (
                                    <button
                                      style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontWeight: 900 }}
                                      onClick={() => window.open(a.mailto, '_blank')}
                                    >
                                      Email
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {m.report?.evidence && (
                          <div style={{ marginTop: 10 }}>
                            <button
                              onClick={() => setShowEvidenceById(prev => ({ ...prev, [m.id]: !prev[m.id] }))}
                              style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontWeight: 900, color: '#0f172a' }}
                            >
                              {showEvidenceById[m.id] ? '🔽 Esconder dados usados' : '🔎 Mostrar dados usados'}
                            </button>
                            {showEvidenceById[m.id] && (
                              <pre style={{ marginTop: 8, background: '#0b1220', color: '#e2e8f0', padding: 10, borderRadius: 12, overflowX: 'auto', fontSize: 12 }}>
                                {JSON.stringify(m.report.evidence, null, 2)}
                              </pre>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {!isUser && Array.isArray(m.documents) && m.documents.length > 0 && (
                      <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 900 }}>📎 Documentos</div>
                        {m.documents.map((d, idx) => (
                          <div key={d.id || idx} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{d.label || d.kind || 'Documento'}</span>
                            {d.uiUrl && (
                              <button
                                style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontWeight: 900 }}
                                onClick={() => window.open(d.uiUrl, '_blank')}
                              >
                                Abrir
                              </button>
                            )}
                            {d.pdfUrl && (
                              <button
                                style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontWeight: 900 }}
                                onClick={() => window.open(d.pdfUrl, '_blank')}
                              >
                                PDF
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  background: '#ffffff',
                  borderRadius: 16,
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  color: '#6b7280',
                  fontWeight: 900
                }}>
                  A analisar…
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', background: '#fff', display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSend();
            }
          }}
          disabled={loading}
          placeholder="Escreve uma pergunta…"
          style={{
            flex: 1,
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid #cbd5e1',
            outline: 'none'
          }}
        />
        <button
          onClick={onSend}
          disabled={loading}
          style={{
            background: loading ? '#a78bfa' : '#8b5cf6',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '0 14px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 900
          }}
        >
          {loading ? 'A enviar…' : 'Enviar'}
        </button>
      </div>
    </div>
  );
};

export default FinancialConsultantChat;
